import { Mail, Shield, FileText, Scale, Info } from "lucide-react"
import { Link } from "react-router-dom"
import { Logo } from "@/components/logo"
import { companyDetails } from "@/lib/company-details"

interface FooterProps {
  mode?: "full" | "brand"
}

export function Footer({ mode = "full" }: FooterProps) {
  const isBrandMode = mode === "brand"

  return (
    <footer className="bg-muted text-foreground">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-12 lg:px-8 lg:py-16">
        {/* Main Footer Content */}
        <div className="grid gap-9 sm:grid-cols-2 lg:grid-cols-4 lg:gap-12">
          {/* Brand Column */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="inline-block transition-opacity hover:opacity-80">
              <Logo variant="main" size="md" />
            </Link>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              Practical tools for international life across borders.
            </p>
            <div className="mt-6 flex gap-3">
              {[
                { label: "Twitter", link: "https://twitter.com/monralabs", path: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" },
                { label: "LinkedIn", link: "https://linkedin.com/company/monralabs", path: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" },
                { label: "Instagram", link: "https://instagram.com/monralabs", path: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" }
              ].map((social) => (
                <a
                  key={social.label}
                  href={social.link}
                  className="flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-background text-muted-foreground transition-colors hover:border-foreground/20 hover:text-foreground"
                  aria-label={social.label}
                >
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d={social.path} />
                  </svg>
                </a>
              ))}
            </div>
          </div>

          <div className="hidden lg:block" aria-hidden="true" />

          {/* About Column */}
          <div>
            <h3 className="text-sm font-semibold text-foreground">Company</h3>
            <ul className="mt-4 space-y-3">
              <li>
                <Link to="/#about" className="group flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground hover:underline">
                  <Info className="h-4 w-4 opacity-50 group-hover:opacity-100" />
                  <span>About Us</span>
                </Link>
              </li>
              <li>
                <a href="mailto:support@monralabs.com" className="group flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground hover:underline">
                  <Mail className="h-4 w-4 opacity-50 group-hover:opacity-100" />
                  <span>Contact Support</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Legal Column */}
          <div>
            <h3 className="text-sm font-semibold text-foreground">Legal</h3>
            <ul className="mt-4 space-y-3">
              <li>
                <Link to="/privacy-policy" className="group flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground hover:underline">
                  <Shield className="h-4 w-4 opacity-50 group-hover:opacity-100" />
                  <span>Privacy Policy</span>
                </Link>
              </li>
              <li>
                <Link to="/terms-of-use" className="group flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground hover:underline">
                  <Scale className="h-4 w-4 opacity-50 group-hover:opacity-100" />
                  <span>Terms of Use</span>
                </Link>
              </li>
              {!isBrandMode && (
                <>
                  <li>
                    <Link to="/esim-service-terms" className="group flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground hover:underline">
                      <Scale className="h-4 w-4 opacity-50 group-hover:opacity-100" />
                      <span>Monra eSIM Service Terms</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/refund-policy" className="group flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground hover:underline">
                      <FileText className="h-4 w-4 opacity-50 group-hover:opacity-100" />
                      <span>Refund Policy</span>
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-10 flex flex-col items-start justify-between gap-5 border-t border-border pt-7 sm:mt-12 sm:flex-row sm:items-end sm:pt-8">
          <div className="max-w-3xl">
            <p className="text-xs leading-5 text-muted-foreground">
              &copy; {new Date().getFullYear()} Monra Labs Ltd. All rights reserved.
            </p>
            <p className="mt-2 text-[0.68rem] leading-5 text-muted-foreground/80">
              {companyDetails.legalName} is registered under company number {companyDetails.companyNumber} in {companyDetails.registeredJurisdiction}. Registered office: {companyDetails.registeredOfficeAddress}.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground sm:gap-4">
            <span>Built for life beyond borders</span>
          </div>
        </div>
      </div>
    </footer>
  )
}
